import useUpdateCharacterCharacteristicsHarness from '../composables/useUpdateCharacterCharacteristicsHarness'
import useUpdateCharacterArmourLocationsHarness from '../composables/useUpdateCharacterArmourLocationsHarness'
import useCreateCharacterArtefactItemsHarness from '../composables/useCreateCharacterArtefactItemsHarness'
import useEditCharacterCampaignNotesHarness from '../composables/useEditCharacterCampaignNotesHarness'

import useUpdateCharacterSkillsHarness from '../composables/useUpdateCharacterSkillsHarness'
import useUpdateCharacterItemsHarness from '../composables/useUpdateCharacterItemsHarness'
import useUpdateCharacterAdjsHarness from '../composables/useUpdateCharacterAdjsHarness'
import useEditCharacterCountsHarness from '../composables/useEditCharacterCountsHarness'
import useEditCharacterSpellsHarness from '../composables/useEditCharacterSpellsHarness'
import useEditCharacterRunesHarness from '../composables/useEditCharacterRunesHarness'

import useEditCharacter from '../composables/useEditCharacter'
import useDeleteCharacter from '../composables/useDeleteCharacter'

import { axios } from '../common/api.service'

import isEmpty from '../helpers/isEmpty'
import hasTempId from '../helpers/hasTempId'
//---------------------------
// character changes harness
//---------------------------
export default async function useCharacterChangesHarness(parms) {
  let createdCharacterSlugForRollback = null
  //
  console.log('------------------------')
  console.log('IN useCharacterChangesHarness')
  console.log('parms:', parms)
  console.log('------------------------')
  //
  try {
    //----------
    // declares
    //----------
    let editCharacterResult = null
    let updateCharacterRunesResults = null
    let updateCharacterItemsResults = null
    let updateCharacterSkillsResults = null
    let updateCharacterSpellsResults = null
    let itemChangesProcessed = false
    // holds Sorcery Casting skill UPDATEs (id=null) for new spells
    let pendingSorceryCastingSkillUpdates = []
    let persistedCharacterCache = null
    const isNewCharacterSave = Boolean(
      parms.changed_profile && parms.changed_profile.payload && !hasExistingCharacterTarget(),
    )
    const initialSaveMpCur = Number(parms.initial_save_mp_cur)
    const hasInitialHpCharacteristicChanges = Boolean(
      isNewCharacterSave &&
        Array.isArray(parms.changed_chars) &&
        parms.changed_chars.some((entry) => {
          const name = (entry?.name ?? '').toString().trim().toLowerCase()
          return name === 'con' || name === 'siz'
        }),
    )
    // shared map for resolving character-item ids by name/display_name (items, runes, adjs)
    const itemIdByName = new Map()
    const runeIdByTempId = new Map()

    function normalizeRuneName(value) {
      return (value ?? '').toString().trim().toLowerCase()
    }

    function normalizeEntityId(value) {
      const normalizedValue = (value ?? '').toString().trim()
      return normalizedValue === '' ? null : normalizedValue
    }

    function getResultDataName(result) {
      return (result?.data?.name ?? '').toString().trim()
    }

    function normalizeComparableValue(value) {
      if (value === undefined || value === null || value === '') {
        return null
      }

      if (typeof value === 'boolean') {
        return value
      }

      if (typeof value === 'number') {
        return Number.isNaN(value) ? null : value
      }

      return String(value).trim()
    }

    function getPersistedCharacterItemStaticId(item) {
      return normalizeEntityId(item?.item ?? item?.item_id ?? item?.item?.id ?? null)
    }

    function getChangedItemStaticId(entry) {
      return normalizeEntityId(entry?.item_id ?? entry?.payload?.item ?? null)
    }

    function getChangedItemMatchLabel(entry) {
      return String(
        entry?.payload?.display_name ??
          entry?.display_name ??
          entry?.payload?.item_name ??
          entry?.name ??
          '',
      ).trim()
    }

    function getArtefactTraceSummary(entry) {
      if (!entry || entry.type !== 'Artefact') {
        return null
      }

      return {
        name: entry?.name ?? null,
        action: entry?.action ?? null,
        trace_id: entry?.payload?.id ?? null,
        item_ref: entry?.payload?.item ?? entry?.item_id ?? null,
        artefact_mode: entry?.payload?.artefact_mode ?? null,
        resolved_character_item_id: entry?._resolved_character_item_id ?? null,
        runes_count: Array.isArray(entry?.payload?.runes) ? entry.payload.runes.length : 0,
        spells_count: Array.isArray(entry?.payload?.spells) ? entry.payload.spells.length : 0,
      }
    }

    function getPersistedCharacterTraceSummary(character) {
      return {
        slug: character?.slug ?? null,
        item_count: Array.isArray(character?.items) ? character.items.length : 0,
        rune_count: Array.isArray(character?.runes) ? character.runes.length : 0,
        spell_count: Array.isArray(character?.spells) ? character.spells.length : 0,
        item_names: Array.isArray(character?.items)
          ? character.items.map((item) => item?.display_name ?? item?.name ?? null).filter(Boolean)
          : [],
      }
    }

    function getPersistedCharacterItemMatchLabel(item) {
      return String(item?.display_name ?? item?.name ?? '').trim()
    }

    function getPersistedCharacterRuneStaticId(rune) {
      return normalizeEntityId(rune?.rune ?? rune?.rune_id ?? rune?.rune?.id ?? null)
    }

    function getChangedRuneStaticId(entry) {
      return normalizeEntityId(entry?.payload?.rune ?? entry?.rune_id ?? null)
    }

    function getChangedRuneMatchLabel(entry) {
      return String(entry?.display_name ?? entry?.name ?? '').trim()
    }

    function getPersistedCharacterRuneMatchLabel(rune) {
      return String(rune?.display_name ?? rune?.name ?? '').trim()
    }

    function getPersistedCharacterRuneLinkedItemId(rune) {
      return normalizeEntityId(
        rune?.character_item ?? rune?.character_item_id ?? rune?.character_item?.id ?? null,
      )
    }

    function getChangedRuneLinkedItemName(entry) {
      return String(entry?.character_item_name ?? entry?.item_name ?? '').trim()
    }

    function isMatchingPersistedRuneAdd(entry, persistedRune) {
      if (!entry || !persistedRune) {
        return false
      }

      const entryStaticRuneId = getChangedRuneStaticId(entry)
      const persistedStaticRuneId = getPersistedCharacterRuneStaticId(persistedRune)

      if (
        entryStaticRuneId !== null &&
        persistedStaticRuneId !== null &&
        entryStaticRuneId !== persistedStaticRuneId
      ) {
        return false
      }

      const entryLabel = getChangedRuneMatchLabel(entry)
      const persistedLabel = getPersistedCharacterRuneMatchLabel(persistedRune)

      if (entryLabel !== '' && persistedLabel !== '' && entryLabel !== persistedLabel) {
        return false
      }

      const entryLinkedItemId = getLinkedCharacterItemIdForChangeEntry(entry)
      const persistedLinkedItemId = getPersistedCharacterRuneLinkedItemId(persistedRune)

      if (
        entryLinkedItemId !== null &&
        persistedLinkedItemId !== null &&
        entryLinkedItemId !== persistedLinkedItemId
      ) {
        return false
      }

      const entryLinkedItemName = getChangedRuneLinkedItemName(entry)
      const persistedLinkedItemName = String(persistedRune?.item_name ?? '').trim()

      if (
        entryLinkedItemId === null &&
        persistedLinkedItemId === null &&
        entryLinkedItemName !== '' &&
        persistedLinkedItemName !== '' &&
        entryLinkedItemName !== persistedLinkedItemName
      ) {
        return false
      }

      const payload = entry?.payload ?? {}
      const comparablePairs = [
        [
          payload?.character ?? null,
          persistedRune?.character ?? persistedRune?.character_id ?? null,
        ],
        [payload?.strength ?? null, persistedRune?.strength ?? null],
        [payload?.pwr_storage ?? null, persistedRune?.pwr_storage ?? null],
        [payload?.starter ?? null, persistedRune?.starter ?? null],
        [payload?.already_integrated ?? null, persistedRune?.already_integrated ?? null],
        [payload?.integrated ?? null, persistedRune?.integrated ?? null],
        [payload?.notes ?? null, persistedRune?.notes ?? null],
      ]

      return comparablePairs.every(([entryValue, persistedValue]) => {
        const normalizedEntryValue = normalizeComparableValue(entryValue)

        if (normalizedEntryValue === null) {
          return true
        }

        return normalizedEntryValue === normalizeComparableValue(persistedValue)
      })
    }

    function findPersistedCharacterItemMatch(items = [], candidate = {}) {
      const candidateId = normalizeEntityId(candidate?.id ?? null)

      if (candidateId !== null) {
        const idMatch = items.find((item) => normalizeEntityId(item?.id ?? null) === candidateId)

        if (idMatch) {
          return idMatch
        }
      }

      const candidateStaticItemId = getPersistedCharacterItemStaticId(candidate)
      const candidateLabel = getPersistedCharacterItemMatchLabel(candidate)

      return items.find((item) => {
        const itemLabel = getPersistedCharacterItemMatchLabel(item)
        const itemStaticItemId = getPersistedCharacterItemStaticId(item)

        if (candidateLabel !== '' && itemLabel !== '' && candidateLabel !== itemLabel) {
          return false
        }

        if (candidateStaticItemId !== null && itemStaticItemId !== null) {
          return candidateStaticItemId === itemStaticItemId
        }

        return candidateLabel !== '' && itemLabel !== '' && candidateLabel === itemLabel
      })
    }

    function isMatchingPersistedItemAdd(entry, persistedItem) {
      if (!entry || !persistedItem) {
        return false
      }

      const isCompositeArtefactCreate = entry?.payload?.artefact_mode === 'CREATE_STATIC'

      const entryStaticItemId = getChangedItemStaticId(entry)
      const persistedStaticItemId = getPersistedCharacterItemStaticId(persistedItem)

      if (
        !isCompositeArtefactCreate &&
        entryStaticItemId !== null &&
        persistedStaticItemId !== null &&
        entryStaticItemId !== persistedStaticItemId
      ) {
        return false
      }

      const entryLabel = getChangedItemMatchLabel(entry)
      const persistedLabel = getPersistedCharacterItemMatchLabel(persistedItem)

      if (entryLabel !== '' && persistedLabel !== '' && entryLabel !== persistedLabel) {
        return false
      }

      const payload = entry?.payload ?? {}
      const comparablePairs = [
        [entry?.type ?? payload?.item_type ?? null, persistedItem?.type ?? null],
        [payload?.artefact_type ?? null, persistedItem?.artefact_type ?? null],
        [payload?.tally_qty ?? null, persistedItem?.tally_qty ?? null],
        [payload?.container ?? null, persistedItem?.container ?? null],
        [payload?.worn_location ?? null, persistedItem?.worn_location ?? null],
        [payload?.attach_location ?? null, persistedItem?.attach_location ?? null],
        [payload?.armour_location ?? null, persistedItem?.armour_location ?? null],
        [payload?.held ?? null, persistedItem?.held ?? null],
        [payload?.equipped ?? null, persistedItem?.equipped ?? null],
        [payload?.player_notes ?? null, persistedItem?.player_notes ?? null],
        [payload?.short_description ?? null, persistedItem?.short_description ?? null],
        [payload?.max_unit_count ?? null, persistedItem?.max_unit_count ?? null],
        [payload?.cur_unit_count ?? null, persistedItem?.cur_unit_count ?? null],
        [payload?.pwr_storage ?? null, persistedItem?.pwr_storage ?? null],
      ]

      return comparablePairs.every(([entryValue, persistedValue]) => {
        const normalizedEntryValue = normalizeComparableValue(entryValue)

        if (normalizedEntryValue === null) {
          return true
        }

        return normalizedEntryValue === normalizeComparableValue(persistedValue)
      })
    }

    async function reconcilePersistedAddItemChanges() {
      if (!Array.isArray(parms.changed_items) || parms.changed_items.length === 0) {
        return new Set()
      }

      const persistedCharacter = await getPersistedCharacterOnce()
      console.log(
        '[RECOVERABLE_ARTEFACT_TRACE] reconcilePersistedAddItemChanges persisted snapshot',
        {
          changed_items_count: parms.changed_items.length,
          persisted_character: getPersistedCharacterTraceSummary(persistedCharacter),
        },
      )
      const persistedItems = Array.isArray(persistedCharacter?.items)
        ? persistedCharacter.items
        : []
      const deletedCharacterItemIds = getDeletedCharacterItemIds()

      if (persistedItems.length === 0) {
        return new Set()
      }

      const reconciledEntries = new Set()

      parms.changed_items.forEach((entry) => {
        if (!entry || entry.action !== 'ADD' || !entry.payload) {
          return
        }

        const matchedPersistedItem = persistedItems.find(
          (persistedItem) =>
            !deletedCharacterItemIds.has(normalizeEntityId(persistedItem?.id ?? null)) &&
            isMatchingPersistedItemAdd(entry, persistedItem),
        )

        if (!matchedPersistedItem?.id) {
          return
        }

        entry._resolved_character_item_id = matchedPersistedItem.id
        entry._resolved_character_item_name = matchedPersistedItem.name ?? null
        entry._resolved_character_item_display_name = matchedPersistedItem.display_name ?? null
        entry.id = matchedPersistedItem.id

        const persistedStaticItemId = getPersistedCharacterItemStaticId(matchedPersistedItem)
        if (persistedStaticItemId !== null) {
          entry.item_id = persistedStaticItemId
          entry.payload.item = persistedStaticItemId
        }

        if (entry.delete_after_persist === true) {
          entry.action = 'DELETE'
          entry.payload = entry.delete_payload ?? undefined
          delete entry.delete_after_persist
          delete entry.delete_payload
          return
        }

        reconciledEntries.add(entry)
      })

      return reconciledEntries
    }

    async function reconcilePersistedAddRuneChanges() {
      if (!Array.isArray(parms.changed_runes) || parms.changed_runes.length === 0) {
        return new Map()
      }

      const persistedCharacter = await getPersistedCharacterOnce()
      const persistedRunes = Array.isArray(persistedCharacter?.runes)
        ? persistedCharacter.runes
        : []

      if (persistedRunes.length === 0) {
        return new Map()
      }

      const reconciledResultsByEntry = new Map()

      parms.changed_runes.forEach((entry) => {
        if (!entry || entry.action !== 'ADD' || !entry.payload) {
          return
        }

        // Persisted runes must stay UPDATE/DELETE operations.
        // If an entry already carries a real id, do not reconcile it as an ADD.
        if (!hasTempId(entry.id) && normalizeEntityId(entry.id) !== null) {
          return
        }

        const matchedPersistedRune = persistedRunes.find((persistedRune) => {
          return isMatchingPersistedRuneAdd(entry, persistedRune)
        })

        if (!matchedPersistedRune?.id) {
          return
        }

        entry._resolved_character_rune_id = matchedPersistedRune.id
        entry.id = matchedPersistedRune.id

        reconciledResultsByEntry.set(entry, {
          data: matchedPersistedRune,
        })
      })

      return reconciledResultsByEntry
    }

    function getPersistedCharacterSlug() {
      return (
        editCharacterResult?.character?.slug ??
        parms?.local_character?.slug ??
        parms?.slug ??
        parms?.changed_profile?.payload?.slug ??
        parms?.changed_profile?.slug ??
        null
      )
    }

    function hasFollowOnPersistenceChanges() {
      return Boolean(
        (Array.isArray(parms?.changed_chars) && parms.changed_chars.length > 0) ||
          (Array.isArray(parms?.changed_items) && parms.changed_items.length > 0) ||
          (Array.isArray(parms?.changed_runes) && parms.changed_runes.length > 0) ||
          (Array.isArray(parms?.changed_skills) && parms.changed_skills.length > 0) ||
          (Array.isArray(parms?.changed_spells) && parms.changed_spells.length > 0) ||
          (Array.isArray(parms?.changed_adjs) && parms.changed_adjs.length > 0) ||
          (Array.isArray(parms?.changed_counts) && parms.changed_counts.length > 0) ||
          (Array.isArray(parms?.changed_campaign_notes) &&
            parms.changed_campaign_notes.length > 0) ||
          (Array.isArray(parms?.changed_armour_locations) &&
            parms.changed_armour_locations.length > 0) ||
          hasInitialHpCharacteristicChanges ||
          (isNewCharacterSave &&
            Number.isFinite(initialSaveMpCur) &&
            !isEmpty(editCharacterResult?.character?.slug)),
      )
    }

    async function getPersistedCharacterOnce() {
      if (persistedCharacterCache) {
        console.log(
          '[RECOVERABLE_ARTEFACT_TRACE] getPersistedCharacterOnce cache hit',
          getPersistedCharacterTraceSummary(persistedCharacterCache),
        )
        return persistedCharacterCache
      }

      const persistedSlug = getPersistedCharacterSlug()
      const canReuseEditCharacterResult = Boolean(
        editCharacterResult?.character && !hasFollowOnPersistenceChanges(),
      )

      console.log('[RECOVERABLE_ARTEFACT_TRACE] getPersistedCharacterOnce resolve source', {
        persisted_slug: persistedSlug,
        can_reuse_edit_character_result: canReuseEditCharacterResult,
        has_follow_on_persistence_changes: hasFollowOnPersistenceChanges(),
        has_edit_character_result: Boolean(editCharacterResult?.character),
      })

      if (canReuseEditCharacterResult) {
        persistedCharacterCache = editCharacterResult.character
        console.log(
          '[RECOVERABLE_ARTEFACT_TRACE] getPersistedCharacterOnce using editCharacterResult',
          getPersistedCharacterTraceSummary(persistedCharacterCache),
        )
        return persistedCharacterCache
      }

      persistedCharacterCache = persistedSlug
        ? await fetchPersistedCharacter(persistedSlug)
        : (editCharacterResult?.character ?? null)

      console.log(
        '[RECOVERABLE_ARTEFACT_TRACE] getPersistedCharacterOnce fetched snapshot',
        getPersistedCharacterTraceSummary(persistedCharacterCache),
      )

      return persistedCharacterCache
    }

    async function fetchPersistedCharacter(slug) {
      if (!slug) {
        return null
      }

      console.log('[RECOVERABLE_ARTEFACT_TRACE] fetchPersistedCharacter request', {
        slug,
      })

      const response = await axios.get(`api/v1/characters/${slug}/`, {
        mode: 'same-origin',
      })

      console.log('[RECOVERABLE_ARTEFACT_TRACE] fetchPersistedCharacter response', {
        slug,
        persisted_character: getPersistedCharacterTraceSummary(response?.data ?? null),
      })

      return response?.data ?? null
    }

    function hasExistingCharacterTarget() {
      return Boolean(
        parms?.changed_profile?.payload?.slug ||
          parms?.changed_profile?.slug ||
          parms?.slug ||
          parms?.changed_profile?.payload?.id ||
          parms?.character_id,
      )
    }
    //=====================================================================
    // build item id by name map helper (for resolving item ids for pending item links by name after item updates)
    //----------------------------------
    function buildItemIdByNameMap() {
      itemIdByName.clear()

      // from new-character response (starter items)
      if (!isEmpty(editCharacterResult)) {
        const newItems = Array.isArray(editCharacterResult.character.items)
          ? editCharacterResult.character.items
          : []
        newItems.forEach((ci) => {
          if (!ci || ci.id == null) return

          const baseName = ci.name
          const displayName = ci.display_name && ci.display_name !== '' ? ci.display_name : null

          // expose base name once (first wins) for backwards compatibility
          if (baseName != null && !itemIdByName.has(baseName)) {
            itemIdByName.set(baseName, ci.id)
          }

          // prefer display_name where present so lookups for e.g. "Pouch 2" work
          if (displayName) {
            itemIdByName.set(displayName, ci.id)
          }
        })
      }

      // from item update responses (new / changed items)
      if (!isEmpty(updateCharacterItemsResults)) {
        updateCharacterItemsResults.forEach((res) => {
          const data = res && res.data
          if (!data || data.id == null) return

          const baseName = data.name
          const displayName =
            data.display_name && data.display_name !== '' ? data.display_name : null

          // do not overwrite existing base-name mapping; rely on display_name for duplicates
          if (baseName != null && !itemIdByName.has(baseName)) {
            itemIdByName.set(baseName, data.id)
          }

          if (displayName) {
            itemIdByName.set(displayName, data.id)
          }
        })
      }

      if (Array.isArray(parms.changed_items)) {
        parms.changed_items.forEach((entry) => {
          const resolvedId = entry?._resolved_character_item_id
          if (resolvedId == null) return

          const aliases = [
            entry?.name,
            entry?.display_name,
            entry?.payload?.item_name,
            entry?.payload?.display_name,
            entry?._resolved_character_item_name,
            entry?._resolved_character_item_display_name,
          ].filter((value) => value != null && value !== '')

          aliases.forEach((alias) => {
            if (!itemIdByName.has(alias)) {
              itemIdByName.set(alias, resolvedId)
            }
          })
        })
      }
    }

    function getDeletedCharacterItemIds() {
      const deletedItemIds = new Set()

      if (!Array.isArray(parms.changed_items)) {
        return deletedItemIds
      }

      parms.changed_items.forEach((entry) => {
        if (!entry || entry.action !== 'DELETE') {
          return
        }

        const resolvedId = entry.id ?? entry._resolved_character_item_id ?? null
        const normalizedId = normalizeEntityId(resolvedId)

        if (normalizedId === null) {
          return
        }

        deletedItemIds.add(normalizedId)
      })

      return deletedItemIds
    }

    function getLinkedCharacterItemIdForChangeEntry(entry) {
      if (!entry) {
        return null
      }

      const rawLinkedItem =
        entry.payload?.character_item ??
        entry.full_payload?.character_item ??
        entry.character_item ??
        entry.character_item_id ??
        entry.item_id ??
        null

      if (rawLinkedItem == null || rawLinkedItem === '') {
        return null
      }

      if (typeof rawLinkedItem === 'object') {
        return normalizeEntityId(rawLinkedItem.id ?? null)
      }

      return normalizeEntityId(rawLinkedItem)
    }

    function isDeleteOwnedByDeletedCharacterItem(entry) {
      if (!entry || entry.action !== 'DELETE') {
        return false
      }

      const deletedCharacterItemIds = getDeletedCharacterItemIds()
      if (deletedCharacterItemIds.size === 0) {
        return false
      }

      const linkedCharacterItemId = getLinkedCharacterItemIdForChangeEntry(entry)
      return linkedCharacterItemId !== null && deletedCharacterItemIds.has(linkedCharacterItemId)
    }

    async function flushRuneUpdatesBeforeDeletedItemSnapshots() {
      if (!Array.isArray(parms.changed_runes) || parms.changed_runes.length === 0) {
        return
      }

      const deletedCharacterItemIds = getDeletedCharacterItemIds()
      if (deletedCharacterItemIds.size === 0) {
        return
      }

      const runeUpdatesToFlush = parms.changed_runes.filter((entry) => {
        if (!entry || entry.action !== 'UPDATE') {
          return false
        }

        const linkedCharacterItemId = normalizeEntityId(
          entry.payload?.character_item ?? entry.full_payload?.character_item ?? null,
        )

        return linkedCharacterItemId !== null && deletedCharacterItemIds.has(linkedCharacterItemId)
      })

      if (runeUpdatesToFlush.length === 0) {
        return
      }

      updateCharacterRunesResults = (updateCharacterRunesResults || []).concat(
        await useEditCharacterRunesHarness(runeUpdatesToFlush),
      )

      const flushedRuneUpdates = new Set(runeUpdatesToFlush)
      parms.changed_runes = parms.changed_runes.filter((entry) => !flushedRuneUpdates.has(entry))
    }

    function buildRuneIdByTempIdMap(runeEntries = [], runeResults = []) {
      if (!Array.isArray(runeEntries) || !Array.isArray(runeResults)) {
        return
      }

      runeEntries.forEach((entry, index) => {
        const resolvedRuneId = runeResults?.[index]?.data?.id ?? null
        const tempRuneIds = [
          entry?.temp_id ?? null,
          entry?.payload?.id ?? null,
          entry?.id ?? null,
        ].filter((value, tempIndex, source) => {
          return hasTempId(value) && source.indexOf(value) === tempIndex
        })

        console.log('[RECOVERABLE_ARTEFACT_TRACE] buildRuneIdByTempIdMap candidate', {
          index,
          rune_name: entry?.name ?? null,
          action: entry?.action ?? null,
          temp_rune_ids: tempRuneIds,
          resolved_rune_id: resolvedRuneId,
          result_name: runeResults?.[index]?.data?.name ?? null,
        })

        if (tempRuneIds.length === 0 || resolvedRuneId == null) {
          console.log('[RECOVERABLE_ARTEFACT_TRACE] buildRuneIdByTempIdMap skipped', {
            index,
            rune_name: entry?.name ?? null,
            temp_rune_ids_count: tempRuneIds.length,
            resolved_rune_id: resolvedRuneId,
          })
          return
        }

        tempRuneIds.forEach((tempRuneId) => {
          runeIdByTempId.set(String(tempRuneId), resolvedRuneId)
        })

        console.log('[RECOVERABLE_ARTEFACT_TRACE] buildRuneIdByTempIdMap stored', {
          index,
          rune_name: entry?.name ?? null,
          temp_rune_ids: tempRuneIds,
          resolved_rune_id: resolvedRuneId,
          rune_temp_id_map_size: runeIdByTempId.size,
        })
      })

      console.log('[RECOVERABLE_ARTEFACT_TRACE] buildRuneIdByTempIdMap complete', {
        rune_temp_id_map_size: runeIdByTempId.size,
        rune_temp_id_map_entries: Array.from(runeIdByTempId.entries()),
      })
    }
    //=====================================================================
    // process item changes before rune maintenance so new runes linked to
    // newly added items can post with a real character_item id.
    //----------------------------------
    async function processItemChanges() {
      if (itemChangesProcessed) {
        return
      }

      if (
        !parms.changed_items ||
        !Array.isArray(parms.changed_items) ||
        parms.changed_items.length === 0
      ) {
        return
      }

      itemChangesProcessed = true

      const reconciledItemAdds = await reconcilePersistedAddItemChanges()

      console.log(
        '[RECOVERABLE_ARTEFACT_TRACE] useCharacterChangesHarness processItemChanges start',
        {
          changed_items_count: Array.isArray(parms.changed_items) ? parms.changed_items.length : 0,
          artefact_items: Array.isArray(parms.changed_items)
            ? parms.changed_items.map((entry) => getArtefactTraceSummary(entry)).filter(Boolean)
            : [],
        },
      )

      console.log('E. IN useCharacterChangesHarness - UPDATE ITEMS')
      //--------------------------------------------------------------------------------------------------------
      // SUMMARY (NEW CHARACTER ONLY):
      //  - map frontend-created items to real ids from add-character response
      //  - inject character id into new item payloads once we know it
      // Existing characters already have real ids and character_id in payload.
      //--------------------------------------------------------------------------------------------------------
      if (!isEmpty(editCharacterResult)) {
        //------------------------------------------------------------------------------------------------------
        // 1. newly created DEFAULT character-item:
        //    - only need character-item-id from character add (insert in header/not payload - as update/patch)
        // 2. ADDITIONAL/non-default item:
        //    - need character-id & item-id (insert in payload - as new record/post)
        //------------------------------------------------------------------------------------------------------
        // loop item changes
        //-------------------
        parms.changed_items.forEach((i) => {
          console.log('----------------------------------------')
          console.log('item before updates:', i)
          //======================================================================================
          // 2. WHERE new character-item (created in frontend)
          //---------------------------------------------------
          if (i.action == 'ADD' && i.payload) {
            console.log('SCENARIO 2...')
            //-----------------------------------------------------------
            // set character id in payload (item id will already be set)
            //-----------------------------------------------------------
            i.payload.character = editCharacterResult.character.id
            const persistedArenaId =
              editCharacterResult.character.arena ??
              editCharacterResult.character.arena_id ??
              i.payload.arena_id ??
              i.payload.arena ??
              null

            if (persistedArenaId != null && persistedArenaId !== '') {
              i.payload.arena_id = persistedArenaId
              i.payload.arena = persistedArenaId
            }

            console.log(
              '2. New character-item detected, setting character id in payload = ',
              i.payload.character,
            )
            console.log(
              '2a. New character-item detected, setting arena in payload = ',
              i.payload.arena_id,
            )
            //=========================================================================================
            // 1. WHERE updating newly created character-item - set character-item id in header
            //----------------------------------------------------------------------------------
          } else {
            console.log('SCENARIO 1...')
            let idx = editCharacterResult.character.items.findIndex((c) => c.name === i.name)
            console.log('1a. Found item name = ', i.name, ' index:', idx)
            if (idx !== -1) {
              i.id = editCharacterResult.character.items[idx].id
              console.log('1b. Updated item with id:', i.id)
            }
          }
        })
      }

      //------------------------------------------------------------------------------------------
      // split Artefact additions that require static Item creation
      // into a separate composite flow while leaving all other item
      // CRUD operations to the existing items harness.
      //------------------------------------------------------------------------------------------
      const artefactAdds = parms.changed_items.filter((i) => {
        if (reconciledItemAdds.has(i)) {
          return false
        }

        return (
          i.type === 'Artefact' &&
          i.action === 'ADD' &&
          i.payload &&
          i.payload.artefact_mode === 'CREATE_STATIC'
        )
      })

      console.log('>>> artefactAdds = ', artefactAdds)

      const normalItemChanges = parms.changed_items.filter((i) => {
        if (reconciledItemAdds.has(i)) {
          return false
        }

        return !(
          i.type === 'Artefact' &&
          i.action === 'ADD' &&
          i.payload &&
          i.payload.artefact_mode === 'CREATE_STATIC'
        )
      })

      console.log('[RECOVERABLE_ARTEFACT_TRACE] useCharacterChangesHarness split item changes', {
        artefact_adds: artefactAdds.map((entry) => getArtefactTraceSummary(entry)),
        normal_item_changes_count: normalItemChanges.length,
      })

      // call update item harness (for non-artefact or static-backed items)
      if (normalItemChanges.length > 0) {
        const normalResults = (await useUpdateCharacterItemsHarness(normalItemChanges)) ?? []
        updateCharacterItemsResults = (updateCharacterItemsResults || []).concat(normalResults)

        normalItemChanges.forEach((entry, idx) => {
          const data = normalResults[idx] && normalResults[idx].data
          if (!data || data.id == null) return

          entry._resolved_character_item_id = data.id
          entry._resolved_character_item_name = data.name ?? null
          entry._resolved_character_item_display_name = data.display_name ?? null
          entry.id = entry.id ?? data.id
        })

        console.log('XXXX UPDATED ITEMS RESULTS (normal):', updateCharacterItemsResults)
        console.log('[RECOVERABLE_ARTEFACT_TRACE] useCharacterChangesHarness normal item results', {
          results_count: normalResults.length,
          artefact_items: normalItemChanges
            .map((entry) => getArtefactTraceSummary(entry))
            .filter(Boolean),
        })
      }

      // call composite artefact harness (Item + CharacterItem)
      if (artefactAdds.length > 0) {
        const artefactResults = (await useCreateCharacterArtefactItemsHarness(artefactAdds)) ?? []
        updateCharacterItemsResults = (updateCharacterItemsResults || []).concat(artefactResults)

        artefactAdds.forEach((entry, idx) => {
          const data = artefactResults[idx] && artefactResults[idx].data
          if (!data || data.id == null) return

          entry._resolved_character_item_id = data.id
          entry._resolved_character_item_name = data.name ?? null
          entry._resolved_character_item_display_name = data.display_name ?? null
          entry.id = entry.id ?? data.id
        })

        console.log('XXXX UPDATED ITEMS RESULTS (artefacts):', updateCharacterItemsResults)
        console.log(
          '[RECOVERABLE_ARTEFACT_TRACE] useCharacterChangesHarness artefact create results',
          {
            results: artefactResults.map((result, index) => ({
              index,
              response_id: result?.data?.id ?? null,
              response_name: result?.data?.name ?? null,
              trace: getArtefactTraceSummary(artefactAdds[index]),
            })),
          },
        )
      }

      console.log('XXXX UPDATED ITEMS RESULTS:', updateCharacterItemsResults)

      //---------------------------------------------------------
      // AFTER ITEMS: process any pending item links by name
      // - container_name (deferred container links)
      // - attach_location_name (deferred attachment links)
      //---------------------------------------------------------
      // (re)build name/display_name -> id map for character items
      buildItemIdByNameMap()

      const containerLinkUpdates = []
      const attachLocationUpdates = []

      parms.changed_items.forEach((entry) => {
        // -------------------------
        // container link-by-name
        // -------------------------
        const containerName = entry && entry.container_name
        if (containerName) {
          // need to distinguish item #1 ('Pouch') from item #2 ('Pouch 2') by using display_name
          const childDisplayName =
            (entry.payload && entry.payload.display_name) || entry.display_name || null
          const childName = childDisplayName ? childDisplayName : entry.name
          // prefer explicit id where available (e.g. existing items), fallback to name map
          const childId =
            entry._resolved_character_item_id != null &&
            !isNaN(Number(entry._resolved_character_item_id))
              ? Number(entry._resolved_character_item_id)
              : entry.id != null && !isNaN(Number(entry.id))
                ? Number(entry.id)
                : itemIdByName.get(childName)
          const containerId = itemIdByName.get(containerName)

          console.log(
            'Processing pending container link for item:',
            entry.name,
            ' container name:',
            containerName,
          )
          console.log('Resolved child id:', childId, ' container id:', containerId)

          if (childId && containerId) {
            const containerLink = {
              name: childName,
              action: 'UPDATE',
              id: childId,
              payload: {
                container: containerId,
              },
            }
            console.log('containerLink = ', containerLink)
            containerLinkUpdates.push(containerLink)
          } else {
            console.warn(
              'Could not resolve ids for container link:',
              'child =',
              childName,
              'container =',
              containerName,
            )
          }
        }

        // ------------------------------
        // attach_location link-by-name
        // ------------------------------
        const attachName = entry && entry.attach_location_name
        // attach_location_name is used after temp attach_location ids are
        // stripped during submission. This can happen for both ADD entries
        // and UPDATE entries on starter items created with the character.
        if (attachName) {
          const childDisplayName =
            (entry.payload && entry.payload.display_name) || entry.display_name || null
          const childName = childDisplayName ? childDisplayName : entry.name
          const childId =
            entry._resolved_character_item_id != null &&
            !isNaN(Number(entry._resolved_character_item_id))
              ? Number(entry._resolved_character_item_id)
              : itemIdByName.get(childName)
          const attachId = itemIdByName.get(attachName)

          console.log(
            'Processing pending attach_location link for item:',
            entry.name,
            ' attach_location name:',
            attachName,
          )
          console.log('Resolved child id:', childId, ' attach_location id:', attachId)

          if (childId && attachId) {
            const attachLink = {
              name: childName,
              action: 'UPDATE',
              id: childId,
              payload: {
                attach_location: attachId,
                held: false,
                container: null,
                worn_location: null,
              },
            }
            console.log('attachLink = ', attachLink)
            attachLocationUpdates.push(attachLink)
          } else {
            console.warn(
              'Could not resolve ids for attach_location link:',
              'child =',
              childName,
              'attach_location =',
              attachName,
            )
          }
        }
      })

      if (containerLinkUpdates.length > 0) {
        console.log(
          'Processing pending item container links:',
          window.$deepCopy(containerLinkUpdates),
        )
        await useUpdateCharacterItemsHarness(containerLinkUpdates)
      }

      if (attachLocationUpdates.length > 0) {
        console.log(
          'Processing pending item attach_location links:',
          window.$deepCopy(attachLocationUpdates),
        )
        await useUpdateCharacterItemsHarness(attachLocationUpdates)
      }

      if (normalItemChanges.some((entry) => entry?.action === 'DELETE')) {
        persistedCharacterCache = null
      }
    }
    //=====================================================================
    // A. add new character
    //----------------------
    if (isNewCharacterSave) {
      console.log('A. IN useCharacterChangesHarness - ADD NEW CHARACTER')
      //---------------------------------------
      // call character api & return chars ids
      //---------------------------------------
      editCharacterResult = await useEditCharacter(parms.changed_profile)
      console.log('NEW CHARACTER RESULT:', editCharacterResult)
      createdCharacterSlugForRollback = editCharacterResult?.character?.slug ?? null

      // Build initial item name -> id map from the newly created
      // character response so that item-linked spells (e.g. sorcery
      // on a starter Belt) can resolve character_item ids even when
      // there are no explicit item changes in this submission.
      buildItemIdByNameMap()
      //===================================================================
      // B. character profile updates
      //------------------------------
    } else if (parms.changed_profile && parms.changed_profile.payload) {
      console.log('IN useCharacterChangesHarness - UPDATE CHARACTER PROFILE')
      //--------------------
      // call character api
      //--------------------
      editCharacterResult = await useEditCharacter(parms.changed_profile)
    }
    //=================================================================
    // B. characteristic updates (e.g. rolled, improvs)
    //--------------------------------------------------
    if (
      parms.changed_chars &&
      Array.isArray(parms.changed_chars) &&
      parms.changed_chars.length > 0
    ) {
      console.log('B. IN useCharacterChangesHarness - UPDATE CHARACTERISTICS')
      //---------------------------------------------------------------
      // update changed_chars with new chars ids (where new character)
      //---------------------------------------------------------------
      if (!isEmpty(editCharacterResult)) {
        parms.changed_chars.forEach((charChange) => {
          let idx = editCharacterResult.character.characteristics.findIndex(
            (c) => c.name === charChange.name,
          )
          if (idx !== -1) {
            charChange.id = editCharacterResult.character.characteristics[idx].id
          }
        })
      }
      //-----------------------------
      // call characteristic harness
      //-----------------------------
      await useUpdateCharacterCharacteristicsHarness(parms.changed_chars)
    }
    await flushRuneUpdatesBeforeDeletedItemSnapshots()
    //===================================================================
    // C0. item maintenance before rune POSTs so rune payloads can carry
    // resolved character_item ids for newly added/renamed items.
    //-------------------------------------------------------------------
    await processItemChanges()
    //==================================================================================================
    // C. rune maintenance (CRUD) [before skills as runes may create runecasting skills - need the ids]
    //--------------------------------------------------------------------------------------------------
    if (
      parms.changed_runes &&
      Array.isArray(parms.changed_runes) &&
      parms.changed_runes.length > 0
    ) {
      console.log('C. IN useCharacterChangesHarness - UPDATE RUNES')
      //-------------------
      // loop rune changes
      //-------------------
      parms.changed_runes.forEach((s) => {
        console.log('rune before updates:', s)
        if (s.payload) {
          let resolvedItemId = null
          const existingCharacterItemId =
            s.payload.character_item ?? s.full_payload?.character_item ?? null

          if (existingCharacterItemId && !hasTempId(existingCharacterItemId)) {
            s.payload.character_item = existingCharacterItemId
          }

          if (s.character_item_temp_id) {
            const linkedChangedItem = (parms.changed_items || []).find(
              (item) => item && item.id === s.character_item_temp_id,
            )

            if (linkedChangedItem?._resolved_character_item_id != null) {
              resolvedItemId = linkedChangedItem._resolved_character_item_id
            }
          }

          const needsItemResolution =
            s.character_item_name &&
            (!s.payload.character_item || hasTempId(s.payload.character_item))

          if (needsItemResolution) {
            const itemId = resolvedItemId ?? itemIdByName.get(s.character_item_name)

            if (itemId) {
              s.payload.character_item = itemId
              if (s.full_payload) {
                s.full_payload.character_item = itemId
              }
              console.log(
                'Resolved rune character_item for',
                s.name,
                'item name =',
                s.character_item_name,
                '-> id =',
                itemId,
              )
            } else {
              console.warn(
                'WARN: Could not resolve character_item id for rune:',
                s.name,
                'item name =',
                s.character_item_name,
              )
            }
          }

          if (
            (s.action === 'ADD' || s.action === 'UPDATE') &&
            (!s.payload.character_item || hasTempId(s.payload.character_item))
          ) {
            throw new Error(`Could not resolve character_item for rune ${s.name}.`)
          }
        }

        if (
          s.action == 'ADD' &&
          s.payload &&
          !s.payload.character &&
          !isEmpty(editCharacterResult)
        ) {
          s.payload.character = editCharacterResult.character.id
          console.log(
            '2. New character-rune detected, setting character id in payload = ',
            s.payload.character,
          )
        }
      })

      for (const entry of parms.changed_runes) {
        const needsPersistedRuneId =
          entry && (entry.action === 'DELETE' || entry.action === 'UPDATE') && !entry.id

        if (!needsPersistedRuneId) {
          continue
        }

        const persistedCharacter = await getPersistedCharacterOnce()
        const persistedRunes = Array.isArray(persistedCharacter?.runes)
          ? persistedCharacter.runes
          : []

        const matchingRune = persistedRunes.find((rune) => {
          if (!rune) return false

          const sameName = normalizeRuneName(rune.name) === normalizeRuneName(entry.name)
          const sameDisplayName =
            normalizeRuneName(rune.display_name) === normalizeRuneName(entry.name)

          return sameName || sameDisplayName
        })

        if (matchingRune?.id != null) {
          entry.id = matchingRune.id
          console.log(
            'Resolved persisted character-rune id for change entry =',
            entry.name,
            '->',
            entry.id,
          )
        }
      }

      //---------------------------------------------------------------------------------------------------------
      // call edit rune harness (need response for new character_rune ids to support new auto-runecasting skills
      //---------------------------------------------------------------------------------------------------------
      const reconciledRuneAddResultsByEntry = await reconcilePersistedAddRuneChanges()

      const runeEntriesForResults = parms.changed_runes.filter((entry) => {
        const shouldSkipDelete = isDeleteOwnedByDeletedCharacterItem(entry)

        if (shouldSkipDelete) {
          console.log(
            'Skipping redundant rune delete after character-item cascade:',
            window.$deepCopy(entry),
          )
        }

        return !shouldSkipDelete
      })

      const runesToUpdate = runeEntriesForResults.filter(
        (entry) => !reconciledRuneAddResultsByEntry.has(entry),
      )

      runeIdByTempId.clear()
      const persistedRuneUpdateResults =
        runesToUpdate.length > 0 ? await useEditCharacterRunesHarness(runesToUpdate) : []
      const persistedRuneUpdateResultsByEntry = new Map(
        runesToUpdate.map((entry, index) => [entry, persistedRuneUpdateResults[index] ?? null]),
      )
      updateCharacterRunesResults = runeEntriesForResults
        .map(
          (entry) =>
            reconciledRuneAddResultsByEntry.get(entry) ??
            persistedRuneUpdateResultsByEntry.get(entry) ??
            null,
        )
        .filter(Boolean)
      console.log('XXXX UPDATED RUNES RESULTS:', updateCharacterRunesResults)
      buildRuneIdByTempIdMap(runeEntriesForResults, updateCharacterRunesResults)

      if (runeEntriesForResults.some((entry) => entry?.action === 'ADD')) {
        persistedCharacterCache = null
      }
    }
    //===================================================================
    // D. skill updates (e.g. ticks, training, new skills, deletions)
    //----------------------------------------------------------------
    if (
      parms.changed_skills &&
      Array.isArray(parms.changed_skills) &&
      parms.changed_skills.length > 0
    ) {
      console.log('D. IN useCharacterChangesHarness - UPDATE SKILLS')
      //-----------------------------------------------------------------
      // Split out Sorcery Casting skill UPDATEs (id=null, non-Rune name)
      // so we can process them AFTER spells (which create casting skills
      // via signals and expose casting_skill ids on the spell results).
      //-----------------------------------------------------------------
      let immediateSkillChanges = []

      parms.changed_skills.forEach((s) => {
        const isUpdate = s && s.action === 'UPDATE'
        const hasNoId = s && (s.id === null || s.id === undefined || s.id === '')
        const isRuneName = !!(s && s.name && s.name.endsWith('Rune'))

        // Sorcery Casting updates have: action UPDATE, id null, name = spell.name,
        // and are NOT rune skills. All such entries were created by
        // convertAddSorceryCastingSkillActions from ADDs.
        if (isUpdate && hasNoId && !isRuneName) {
          console.log('D. Detected pending Sorcery Casting skill UPDATE:', s)
          pendingSorceryCastingSkillUpdates.push(s)
        } else {
          immediateSkillChanges.push(s)
        }
      })

      // If we stripped out all skills (only sorcery casting), we skip the
      // main skills harness here and will handle sorcery casting later.
      if (immediateSkillChanges.length > 0) {
        //---------------------------------------------------------------------------------------------------------
        // where (a) new character or (b) new runecasting skill for new runes (otherwise skill ids already known)
        //---------------------------------------------------------------------------------------------------------
        if (!isEmpty(editCharacterResult) || !isEmpty(updateCharacterRunesResults)) {
          //-------------------------------------------------------------------------------------------------------------------
          // where ticks/training provided for:
          //-----------------------------------
          // 1. newly created DEFAULT character-skill:
          //    - only need character-skill-id from character add (insert in header/not payload - as update/patch)
          // 2. ADDITIONAL/non-default skill (e.g. specialism):
          //    - need character-id & skill-id (insert in payload - as new record/post)
          // 3. NEW CHARACTER - ADDITIONAL runecasting skills auto-created in backend for new character-runes (e.g. Air Rune):
          //    - need skill-id (top-level - as update/patch)
          //-------------------------------------------------------------------------------------------------------------------
          // loop skill changes (excluding Sorcery Casting updates)
          //------------------------------------------------------
          immediateSkillChanges.forEach((s) => {
            console.log('----------------------------------------')
            console.log('skill before updates:', s)
            //========================================================================================
            // 3. WHERE NOT ADD runecasting character-skill
            //----------------------------------------------
            if ((s.action == 'UPDATE' || s.action == 'DELETE') && s.name.endsWith('Rune')) {
              console.log('SCENARIO 3...')
              const runeResults = Array.isArray(updateCharacterRunesResults)
                ? updateCharacterRunesResults
                : []
              //----------------------------------------------------
              // find character-skill in updated runes by rune name
              //----------------------------------------------------
              let idx = runeResults.findIndex((r) => getResultDataName(r) === s.name)
              //-------------------------------------
              // set character-skill id at top-level
              //-------------------------------------
              console.log('3a. Found skill name = ', s.name, ' index:', idx)
              if (idx !== -1 && runeResults[idx]?.data?.character_skill?.id) {
                s.id = runeResults[idx].data.character_skill.id
                console.log('3b. Updated skill with id:', s.id)
              } else if (s.id !== undefined && s.id !== null && s.id !== '') {
                console.log('3b. Keeping existing rune skill id:', s.id)
              } else if (
                !isEmpty(editCharacterResult) &&
                Array.isArray(editCharacterResult.character?.skills)
              ) {
                const persistedSkillIdx = editCharacterResult.character.skills.findIndex(
                  (c) => c.name === s.name,
                )

                console.log(
                  '3b. Fallback persisted rune skill lookup index for',
                  s.name,
                  '=',
                  persistedSkillIdx,
                )

                if (persistedSkillIdx !== -1) {
                  s.id = editCharacterResult.character.skills[persistedSkillIdx].id
                  console.log('3c. Resolved rune skill id from persisted character:', s.id)
                } else {
                  console.log(
                    '3d. ERROR: Could not find persisted rune skill for rune name:',
                    s.name,
                  )
                }
              } else {
                console.log('3e. ERROR: Could not find character-skill for rune name:', s.name)
              }
              //======================================================================================
              // 2. WHERE new character-skill (created in frontend)
              //----------------------------------------------------
            } else if (s.action == 'ADD' && s.payload) {
              console.log('SCENARIO 2...')
              //------------------------------------------------------------
              // set character id in payload (skill id will already be set)
              //------------------------------------------------------------
              s.payload.character = editCharacterResult.character.id
              console.log(
                '2. New character-skill detected, setting character id in payload = ',
                s.payload.character,
              )
              //=========================================================================================
              // 1. WHERE updating newly created character-skill - set character-skill id in header
              //------------------------------------------------------------------------------------
            } else {
              console.log('SCENARIO 1...')
              if (isEmpty(editCharacterResult)) {
                if (s.id !== undefined && s.id !== null && s.id !== '') {
                  console.log(
                    '1a. Existing character-skill already has id; keeping current id for',
                    s.name,
                    'id =',
                    s.id,
                  )
                } else {
                  console.warn(
                    '1a. No editCharacterResult available to resolve character-skill id for',
                    s.name,
                  )
                }
                return
              }

              let idx = editCharacterResult.character.skills.findIndex((c) => c.name === s.name)
              console.log('1b. Found skill name = ', s.name, ' index:', idx)
              if (idx !== -1) {
                s.id = editCharacterResult.character.skills[idx].id
                console.log('1c. Updated skill with id:', s.id)
              }
            }
          })
        }
        //--------------------------------------------------------------------------------------------
        // call update skill harness (need response for new character_skill ids to support skill adjs
        //--------------------------------------------------------------------------------------------
        // NOTE: runecasting skills are now retained when their backing rune is
        // deleted/unintegrated, so rune maintenance should not imply a companion
        // character-skill DELETE.
        //
        // Filter out any runecasting skill DELETE that has a matching rune DELETE
        // in this submission; rune maintenance (section C) has already run.
        let skillsToUpdate = immediateSkillChanges

        const deletedItemLinkedSpellNames = new Set(
          (parms.changed_spells || [])
            .filter((entry) => isDeleteOwnedByDeletedCharacterItem(entry))
            .map((entry) => (entry?.name ?? '').toString().trim())
            .filter((name) => name !== ''),
        )

        if (deletedItemLinkedSpellNames.size > 0) {
          skillsToUpdate = skillsToUpdate.filter((entry) => {
            const skillName = (entry?.name ?? '').toString().trim()
            const shouldSkipDelete =
              entry?.action === 'DELETE' && deletedItemLinkedSpellNames.has(skillName)

            if (shouldSkipDelete) {
              console.log(
                'D. Skipping DELETE for casting skill because backing item spell is already removed by item delete:',
                skillName,
              )
            }

            return !shouldSkipDelete
          })
        }

        if (
          parms.changed_runes &&
          Array.isArray(parms.changed_runes) &&
          parms.changed_runes.length > 0
        ) {
          const deletedRuneNames = new Set(
            parms.changed_runes.filter((r) => r.action === 'DELETE').map((r) => r.name),
          )

          skillsToUpdate = skillsToUpdate.filter((s) => {
            const isRuneCastingSkillDelete =
              s && s.action === 'DELETE' && s.name && s.name.endsWith('Rune')

            if (isRuneCastingSkillDelete && deletedRuneNames.has(s.name)) {
              console.log(
                'D. Skipping DELETE for runecasting skill because rune delete already handled:',
                s.name,
              )
              return false
            }

            return true
          })
        }

        updateCharacterSkillsResults = await useUpdateCharacterSkillsHarness(skillsToUpdate)
        console.log('XXXX UPDATED SKILLS RESULTS:', updateCharacterSkillsResults)
      }
    }
    //===================================================================
    // E. item updates (e.g. tally_qty, notes, additions, deletions)
    //---------------------------------------------------------------
    if (
      parms.changed_items &&
      Array.isArray(parms.changed_items) &&
      parms.changed_items.length > 0
    ) {
      await processItemChanges()
    }
    //=====================================================================
    // E2. rune -> item links (for items created/updated above)
    //---------------------------------------------------------------------
    // For new characters (or existing chars with new items), some
    // character-runes were created before items had real ids. During
    // submission we captured character_item_name on each rune. Now that
    // items exist and itemIdByName is populated, resolve and patch the
    // character_item field on those runes.
    if (
      parms.changed_runes &&
      Array.isArray(parms.changed_runes) &&
      parms.changed_runes.length > 0 &&
      !isEmpty(updateCharacterRunesResults) &&
      itemIdByName.size > 0
    ) {
      const runeItemLinkUpdates = []

      parms.changed_runes.forEach((r) => {
        const itemName = r && r.character_item_name
        let itemId = null

        if (r && r.character_item_temp_id) {
          const linkedChangedItem = (parms.changed_items || []).find(
            (item) => item && item.id === r.character_item_temp_id,
          )

          if (linkedChangedItem?._resolved_character_item_id != null) {
            itemId = linkedChangedItem._resolved_character_item_id
          }
        }

        if (!itemId && !itemName) return

        if (!itemId) {
          itemId = itemIdByName.get(itemName)
        }

        if (!itemId) {
          console.warn(
            'Could not resolve item id for rune-item link:',
            'rune =',
            r.name,
            'item name =',
            itemName,
          )
          return
        }

        if (r.full_payload) {
          r.full_payload.character_item = itemId
        }

        // find the created/updated character-rune response by rune name
        const idx = updateCharacterRunesResults.findIndex((res) => {
          const data = res && res.data
          return data && data.name === r.name
        })

        if (idx === -1) {
          console.warn(
            'Could not find character-rune in results for rune-item link:',
            'rune =',
            r.name,
          )
          return
        }

        const runeId = updateCharacterRunesResults[idx].data.id
        const link = {
          name: r.name,
          action: 'UPDATE',
          id: runeId,
          payload: {
            character_item: itemId,
          },
        }

        console.log(
          'Queued rune-item link update:',
          'rune =',
          r.name,
          'runeId =',
          runeId,
          'itemId =',
          itemId,
        )
        runeItemLinkUpdates.push(link)
      })

      if (runeItemLinkUpdates.length > 0) {
        console.log('Processing pending rune item links:', window.$deepCopy(runeItemLinkUpdates))
        await useEditCharacterRunesHarness(runeItemLinkUpdates)
      }
    }
    //=========================================================================
    // F. spell maintenance (CRUD)
    //---------------------------------------------------------------------
    if (
      parms.changed_spells &&
      Array.isArray(parms.changed_spells) &&
      parms.changed_spells.length > 0
    ) {
      console.log('F. IN useCharacterChangesHarness - UPDATE SPELLS')
      console.log(
        '[RECOVERABLE_ARTEFACT_TRACE] useCharacterChangesHarness before spell persistence',
        {
          changed_spells_count: parms.changed_spells.length,
          changed_runes_count: Array.isArray(parms.changed_runes) ? parms.changed_runes.length : 0,
          changed_adjs_count: Array.isArray(parms.changed_adjs) ? parms.changed_adjs.length : 0,
        },
      )

      // Where we have a new character and/or newly created items/runes, we
      // need to inject the real backend ids into spell payloads before
      // sending them to the appropriate spell endpoints.

      let persistedRunesForSpellResolution = []
      const hasRuneSpellNeedingResolution = parms.changed_spells.some((entry) => {
        const spellType = (entry?.type || '').toString().toLowerCase()

        return (
          spellType === 'rune' &&
          entry?.payload &&
          (!entry.payload.character_rune || hasTempId(entry.payload.character_rune))
        )
      })

      if (
        hasRuneSpellNeedingResolution &&
        isEmpty(editCharacterResult) &&
        runeIdByTempId.size === 0
      ) {
        const persistedCharacter = await getPersistedCharacterOnce()
        persistedRunesForSpellResolution = Array.isArray(persistedCharacter?.runes)
          ? persistedCharacter.runes
          : []

        console.log(
          '[RECOVERABLE_ARTEFACT_TRACE] loaded persisted runes for spell resolution fallback',
          {
            persisted_runes_count: persistedRunesForSpellResolution.length,
            persisted_rune_labels: persistedRunesForSpellResolution.map((rune) => ({
              id: rune?.id ?? null,
              name: rune?.name ?? null,
              display_name: rune?.display_name ?? null,
            })),
          },
        )
      }

      parms.changed_spells.forEach((s) => {
        console.log('spell before updates:', window.$deepCopy(s))

        const spellType = (s.type || '').toString().toLowerCase()

        // ------------------------------------------------------------------
        // 1. Direct character spells for a NEW character
        //    Ensure the payload has the character id when adding.
        // ------------------------------------------------------------------
        if (
          spellType === 'character' &&
          s.action === 'ADD' &&
          s.payload &&
          !s.payload.character &&
          !isEmpty(editCharacterResult)
        ) {
          s.payload.character = editCharacterResult.character.id
          console.log(
            '1. New character spell detected, setting character id in payload = ',
            s.payload.character,
          )
        }

        // ------------------------------------------------------------------
        // 2. Item-based spells where the spell was linked to a temp
        //    character-item. processSubmission captured character_item_name;
        //    now resolve it to a real character_item id via itemIdByName.
        // ------------------------------------------------------------------
        if (spellType === 'item' && s.payload) {
          const itemName = s.character_item_name
          const needsResolution =
            itemName &&
            itemIdByName.size > 0 &&
            (!s.payload.character_item || hasTempId(s.payload.character_item))

          if (needsResolution) {
            const itemId = itemIdByName.get(itemName)
            console.log(
              '2. Resolving item-based spell link for',
              s.name,
              'itemName =',
              itemName,
              '-> id =',
              itemId,
            )
            if (itemId) {
              s.payload.character_item = itemId
            } else {
              console.warn(
                'WARN: Could not resolve character_item id for spell:',
                s.name,
                'item name =',
                itemName,
              )
            }
          }
        }

        // ------------------------------------------------------------------
        // 3. Rune-based spells where the spell was linked to a temp
        //    character-rune id. processSubmission captured
        //    character_rune_name; now resolve it using either the rune
        //    update results or (for new characters) the character add
        //    response.
        // ------------------------------------------------------------------
        if (
          spellType === 'rune' &&
          s.payload &&
          (!s.payload.character_rune || hasTempId(s.payload.character_rune))
        ) {
          const tempRuneId = s.payload.character_rune ?? null
          const runeName = s.character_rune_name
          const hadTempRuneId = hasTempId(tempRuneId)

          let resolvedRuneId = null
          let resolvedRuneSource = null

          if (hadTempRuneId) {
            resolvedRuneId = runeIdByTempId.get(String(tempRuneId)) ?? null
            if (resolvedRuneId) {
              resolvedRuneSource = 'temp-id-map'
            }
          }

          // Prefer ids from rune maintenance run in this harness
          if (!resolvedRuneId && runeName && !isEmpty(updateCharacterRunesResults)) {
            const idx = updateCharacterRunesResults.findIndex((res) => {
              const data = res && res.data
              if (!data) return false
              const dataName = data.display_name || data.name
              return dataName === runeName
            })

            if (idx !== -1) {
              resolvedRuneId = updateCharacterRunesResults[idx].data.id
              resolvedRuneSource = 'rune-results-name-match'
            }
          }

          // Fallback: for a new character, use runes from the
          // editCharacterResult payload.
          if (!resolvedRuneId && runeName && !isEmpty(editCharacterResult)) {
            const runes = Array.isArray(editCharacterResult.character.runes)
              ? editCharacterResult.character.runes
              : []
            const rIdx = runes.findIndex((r) => {
              const dataName = (r && r.display_name) || (r && r.name) || null
              return dataName === runeName
            })
            if (rIdx !== -1) {
              resolvedRuneId = runes[rIdx].id
              resolvedRuneSource = 'new-character-rune-name-match'
            }
          }

          if (!resolvedRuneId && runeName && persistedRunesForSpellResolution.length > 0) {
            const normalizedRuneName = normalizeRuneName(runeName)
            const persistedRune = persistedRunesForSpellResolution.find((r) => {
              return (
                normalizeRuneName(r?.display_name) === normalizedRuneName ||
                normalizeRuneName(r?.name) === normalizedRuneName
              )
            })

            if (persistedRune?.id != null) {
              resolvedRuneId = persistedRune.id
              resolvedRuneSource = 'persisted-character-rune-name-match'
            }
          }

          console.log('[RECOVERABLE_ARTEFACT_TRACE] rune spell resolution check', {
            spell_name: s.name ?? null,
            temp_rune_id: tempRuneId,
            has_temp_rune_id: hadTempRuneId,
            rune_name: runeName ?? null,
            rune_temp_id_map_size: runeIdByTempId.size,
            rune_temp_id_map_hit:
              hadTempRuneId && tempRuneId != null ? runeIdByTempId.has(String(tempRuneId)) : false,
            update_rune_results_count: Array.isArray(updateCharacterRunesResults)
              ? updateCharacterRunesResults.length
              : 0,
            resolved_rune_id: resolvedRuneId,
            resolved_rune_source: resolvedRuneSource,
          })

          console.log(
            '3. Resolving rune-based spell link for',
            s.name,
            'runeName =',
            runeName,
            '-> id =',
            resolvedRuneId,
          )

          if (resolvedRuneId) {
            s.payload.character_rune = resolvedRuneId
          } else {
            console.warn(
              'WARN: Could not resolve character_rune id for spell:',
              s.name,
              'rune name =',
              runeName,
            )
          }
        }
      })

      let spellsToUpdate = parms.changed_spells.filter((entry) => {
        const shouldSkipDelete = isDeleteOwnedByDeletedCharacterItem(entry)

        if (shouldSkipDelete) {
          console.log(
            'Skipping redundant spell delete after character-item cascade:',
            window.$deepCopy(entry),
          )
        }

        return !shouldSkipDelete
      })

      if (
        parms.changed_runes &&
        Array.isArray(parms.changed_runes) &&
        parms.changed_runes.length > 0
      ) {
        const deletedRuneNames = new Set(
          parms.changed_runes
            .filter((r) => r && r.action === 'DELETE')
            .map((r) => normalizeRuneName(r.name))
            .filter((name) => name !== ''),
        )

        if (deletedRuneNames.size > 0) {
          spellsToUpdate = spellsToUpdate.filter((s) => {
            const isRuneSpellDelete =
              s && s.action === 'DELETE' && (s.type || '').toString().toLowerCase() === 'rune'

            if (!isRuneSpellDelete) {
              return true
            }

            const linkedRuneNames = [
              normalizeRuneName(s.character_rune_name),
              normalizeRuneName(s.casting_rune_name),
              normalizeRuneName(s.secondary_rune_name),
            ].filter((name) => name !== '')

            const shouldSkipDelete = linkedRuneNames.some((name) => deletedRuneNames.has(name))

            if (shouldSkipDelete) {
              console.log(
                'F. Skipping rune spell DELETE because the backing rune is already being deleted:',
                s.name,
                linkedRuneNames,
              )
              return false
            }

            return true
          })
        }
      }

      console.log('[RECOVERABLE_ARTEFACT_TRACE] final spellsToUpdate before persistence', {
        spells_count: Array.isArray(spellsToUpdate) ? spellsToUpdate.length : 0,
        spells: (spellsToUpdate || []).map((spell) => ({
          name: spell?.name ?? null,
          action: spell?.action ?? null,
          type: spell?.type ?? null,
          id: spell?.id ?? null,
          character_rune_name: spell?.character_rune_name ?? null,
          casting_rune_name: spell?.casting_rune_name ?? null,
          payload_character_rune: spell?.payload?.character_rune ?? null,
          payload_character_item: spell?.payload?.character_item ?? null,
        })),
      })

      // Finally, call the spell harness to POST/PATCH/DELETE against the
      // correct endpoints (character, item, rune) as selected inside
      // useEditCharacterSpell.
      updateCharacterSpellsResults = await useEditCharacterSpellsHarness(spellsToUpdate)
      console.log('XXXX UPDATED SPELLS RESULTS:', updateCharacterSpellsResults)

      //------------------------------------------------------------------
      // After spells are created, resolve Sorcery Casting updates (id=null)
      // to the real casting_skill ids exposed on the spell responses, and
      // then PATCH those CharacterSkill rows.
      //------------------------------------------------------------------
      if (
        pendingSorceryCastingSkillUpdates &&
        Array.isArray(pendingSorceryCastingSkillUpdates) &&
        pendingSorceryCastingSkillUpdates.length > 0 &&
        !isEmpty(updateCharacterSpellsResults)
      ) {
        console.log(
          'Processing pending Sorcery Casting skill updates after spell maintenance:',
          window.$deepCopy(pendingSorceryCastingSkillUpdates),
        )

        pendingSorceryCastingSkillUpdates.forEach((entry) => {
          const spellName = entry && entry.name
          if (!spellName) return

          const idx = updateCharacterSpellsResults.findIndex((res) => {
            const data = res && res.data
            if (!data) return false
            // CharacterSpellSerializer exposes name = static spell name and casting_skill id
            return data.name === spellName && data.casting_skill
          })

          if (idx === -1) {
            console.warn(
              'Could not resolve casting_skill id for Sorcery Casting skill update with spell name:',
              spellName,
            )
            return
          }

          const castingSkillId = updateCharacterSpellsResults[idx].data.casting_skill
          console.log(
            'Resolved casting_skill id for Sorcery Casting skill update:',
            spellName,
            '->',
            castingSkillId,
          )
          entry.id = castingSkillId
        })

        // Filter out any entries we still couldn't resolve an id for
        const sorcerySkillsToUpdate = pendingSorceryCastingSkillUpdates.filter((s) => s && s.id)

        if (sorcerySkillsToUpdate.length > 0) {
          console.log(
            'Updating Sorcery Casting CharacterSkills with resolved ids:',
            window.$deepCopy(sorcerySkillsToUpdate),
          )
          await useUpdateCharacterSkillsHarness(sorcerySkillsToUpdate)
        } else {
          console.log('No Sorcery Casting skill updates required after id resolution')
        }
      }
    }

    //=========================================================================
    // G. adjustment updates (characteristic, skill, ability, runecasting)
    //---------------------------------------------------------------------
    if (parms.changed_adjs && Array.isArray(parms.changed_adjs) && parms.changed_adjs.length > 0) {
      console.log('E. IN useCharacterChangesHarness - UPDATE ADJUSTMENTS')
      //---------------------------------------------------------------------------------------------------------------------
      // where (a) new character OR (b) character skill updates OR (c) character runes updates (with new runecasting skills)
      //---------------------------------------------------------------------------------------------------------------------
      if (
        !isEmpty(editCharacterResult) ||
        !isEmpty(updateCharacterItemsResults) ||
        !isEmpty(updateCharacterSkillsResults) ||
        !isEmpty(updateCharacterRunesResults)
      ) {
        console.log('>>>>> E. Updating adjs payloads with new ids')
        //----------------------------------------------------------------------
        // update changed_adjs with appropriate new ids aligned to adj category
        //----------------------------------------------------------------------
        parms.changed_adjs.forEach((adj) => {
          console.log('E. ==================================================================')
          console.log('E. ==================================================================')
          console.log('>>>>> E. Processing adj:', window.$deepCopy(adj))
          console.log('>>>>> E. Category:', adj.category)
          //==================================================================================
          // CATEGORY: characteristic (CharacterCharacteristicAdjustment)
          //==================================================================================
          if (adj.category === 'characteristic' || adj.category === 'characteristic-skill') {
            console.log('E0. CHARACTERISTIC ADJ BEFORE UPDATE:', window.$deepCopy(adj))
            // Only use editCharacterResult for NEW characters; for existing
            // characters the characteristic id was already resolved in
            // getAdjChanges/getAdjPayloadIds using the original character
            // object, and editCharacterResult will be null.
            if (!isEmpty(editCharacterResult)) {
              let char_idx = editCharacterResult.character.characteristics.findIndex(
                (c) => c.name === adj.name,
              )
              if (char_idx !== -1) {
                adj.payload.character_characteristic =
                  editCharacterResult.character.characteristics[char_idx].id
              }
            }
            //==================================================================================
            // CATEGORY: skill (CharacterSkillAdjustment)
            //==================================================================================
          } else if (adj.category === 'skill') {
            console.log('E1. SKILL ADJ BEFORE UPDATE:', window.$deepCopy(adj))
            //-------------------------------------------------------------------------------------
            // ignore where character_skill (id) already set ie. new/update adj for existing skill
            //-------------------------------------------------------------------------------------
            const hasTmpSkillId =
              adj.payload &&
              typeof adj.payload.character_skill === 'string' &&
              adj.payload.character_skill.startsWith('TMP-')

            if (adj.payload && (isEmpty(adj.payload.character_skill) || hasTmpSkillId)) {
              console.log('E2. Processing SKILL adj:', window.$deepCopy(adj))
              //=====================================================================================================================
              // A. where adjs for new runecasting skill following new rune creation for (a) existing character OR (b) new character
              //=====================================================================================================================
              console.log(
                'E3. updateCharacterRunesResults = ',
                window.$deepCopy(updateCharacterRunesResults),
              )
              if (adj.action == 'ADD' && adj.payload && adj.name.endsWith('Rune')) {
                console.log('E4. New runecasting skill adj detected for rune:', adj.name)
                //----------------------------------------------------
                // find character-skill in updated runes by rune name
                //----------------------------------------------------
                let idx = updateCharacterRunesResults.findIndex(
                  (r) => getResultDataName(r) === adj.name,
                )
                //-----------------------------
                // check character-skill found
                //-----------------------------
                if (idx !== -1) {
                  console.log(
                    'E4a. Found rune at index:',
                    idx,
                    'data:',
                    updateCharacterRunesResults[idx].data,
                  )
                  //-------------------------
                  // isolate character-skill
                  //-------------------------
                  const characterSkillId = updateCharacterRunesResults[idx].data.character_skill.id
                  console.log('E4b. Character skill id from rune response:', characterSkillId)
                  //-----------------------------------
                  // set character-skill id in payload
                  //-----------------------------------
                  adj.payload.character_skill = characterSkillId
                  console.log(
                    'E4c. Set adj.payload.character_skill to:',
                    adj.payload.character_skill,
                  )
                } else {
                  console.error(
                    'E4d. ERROR: Could not find rune in results for adj name:',
                    adj.name,
                  )
                }
                //===========================================================================================================
                // B. where adjs for (a) existing character with new skills OR (b) new character with non-default skill adjs
                //===========================================================================================================
              } else if (!isEmpty(updateCharacterSkillsResults)) {
                console.log('E5. updateCharacterSkillsResults = ', updateCharacterSkillsResults)
                //--------------------------------------------------
                // get index in new character skills for skill name
                //--------------------------------------------------
                let idx = updateCharacterSkillsResults.findIndex(
                  (r) => getResultDataName(r) === adj.name,
                )
                //-----------
                // not found
                //-----------
                if (idx === -1) {
                  console.warn('>>>>> Skill not found in updated skill results for adj:', adj.name)
                  console.warn(
                    '>>>>> Available updated skills:',
                    updateCharacterSkillsResults.map((r) => getResultDataName(r)),
                  )

                  if (!isEmpty(editCharacterResult)) {
                    const persistedSkillIdx = editCharacterResult.character.skills.findIndex(
                      (c) => c.name === adj.name,
                    )

                    if (persistedSkillIdx !== -1) {
                      console.log(
                        '>>>>> Falling back to persisted character skill id (new char):',
                        editCharacterResult.character.skills[persistedSkillIdx].id,
                      )
                      adj.payload.character_skill =
                        editCharacterResult.character.skills[persistedSkillIdx].id
                    }
                  }
                } else {
                  console.log('>>>>> Found character skill index (existing char):', idx)
                  //-------------------------------------------
                  // found - set character_skill id in payload
                  //-------------------------------------------
                  console.log(
                    '>>>>> E. Found character skill id (existing char):',
                    updateCharacterSkillsResults[idx].data.id,
                  )
                  adj.payload.character_skill = updateCharacterSkillsResults[idx].data.id
                }
                //=======================================================================================
                // B. where new character
                //=======================================================================================
              } else if (!isEmpty(editCharacterResult)) {
                console.log('E6. updateCharacterRunesResults = ', updateCharacterRunesResults)
                console.log('>>>>> New character detected - looking up skill id')
                //--------------------------------------------------
                // get index in new character skills for skill name
                //--------------------------------------------------
                let idx = editCharacterResult.character.skills.findIndex((c) => c.name === adj.name)
                console.log('>>>>> Found character skill index (new char):', idx)
                //-----------
                // not found
                //-----------
                if (idx === -1) {
                  console.log('>>>>> Character skill not found for adj:', adj)
                } else {
                  //------------------------------------------
                  // found - set character_skill id in payload
                  //-------------------------------------------
                  console.log(
                    '>>>>> Found character skill id (new char):',
                    editCharacterResult.character.skills[idx].id,
                  )
                  adj.payload.character_skill = editCharacterResult.character.skills[idx].id
                }
              }
            } else {
              console.log(
                '>>>>> Character skill id already set in payload - no action needed for adj:',
                adj,
              )
            }
            //==================================================================================
            // CATEGORY: ability (CharacterAbilityAdjustment)
            //==================================================================================
          } else if (adj.category === 'ability') {
            // For NEW characters we need to inject the character id from
            // editCharacterResult; for existing characters this was already
            // populated in getAdjPayloadIds and editCharacterResult is null.
            if (!isEmpty(editCharacterResult)) {
              adj.payload.character = editCharacterResult.character.id
            }
          }
          //-------------------------------------------------------------------
          // ITEM LINK RESOLUTION (character_item)
          //-------------------------------------------------------------------
          // For new characters, some adjustments were created against items
          // that only had temp ids on the frontend. getAdjChanges captured a
          // stable item link key (character_item_link_name) using the item's
          // name/display_name. Now that items have real ids and the
          // itemIdByName map is populated, resolve character_item here.
          if (adj && adj.character_item_link_name && itemIdByName.size > 0) {
            const linkName = adj.character_item_link_name
            const resolvedItemId = itemIdByName.get(linkName)
            console.log(
              '>>>>> E. Resolving character_item for adj via name:',
              linkName,
              ' -> id:',
              resolvedItemId,
            )
            if (resolvedItemId) {
              if (!adj.payload) adj.payload = {}
              adj.payload.character_item = resolvedItemId
            } else {
              console.warn(
                '>>>>> E. WARN: Could not resolve character_item for adj with link name:',
                linkName,
              )
            }
          }
          console.log('adj after update:', adj)
        })
      }
      //--------------------------
      // call adjustments harness
      //--------------------------
      const filteredChangedAdjs = parms.changed_adjs.filter((adj) => {
        if (!adj || adj.action !== 'DELETE') {
          return true
        }

        const shouldSkipDelete = isDeleteOwnedByDeletedCharacterItem(adj)

        if (shouldSkipDelete) {
          console.log(
            'Skipping redundant adjustment delete after character-item cascade:',
            window.$deepCopy(adj),
          )
        }

        return !shouldSkipDelete
      })

      await useUpdateCharacterAdjsHarness(filteredChangedAdjs)
    }
    //==================================================================================================
    // G. count maintenance (CRUD)
    //-----------------------------
    if (
      parms.changed_counts &&
      Array.isArray(parms.changed_counts) &&
      parms.changed_counts.length > 0
    ) {
      console.log('C. IN useCharacterChangesHarness - UPDATE COUNTS')
      //---------------------------------------------------
      // where new character (otherwise ids already known)
      //---------------------------------------------------
      if (!isEmpty(editCharacterResult)) {
        //-------------------
        // loop count changes
        //-------------------
        parms.changed_counts.forEach((s) => {
          console.log('count before updates:', s)
          //-----------------------------
          // 2. where new character-count
          //-----------------------------
          if (s.action === 'ADD' && s.payload) {
            //-----------------------------------------------------------
            // set character id in payload (id will be temp/stripped)
            //-----------------------------------------------------------
            s.payload.character = editCharacterResult.character.id
            console.log(
              '2. New character-count detected, setting character id in payload = ',
              s.payload.character,
            )
          } else if (
            (s.action === 'UPDATE' || s.action === 'DELETE') &&
            (!s.id || hasTempId(s.id))
          ) {
            //-----------------------------------------------------------------
            // existing starter counts for a NEW character:
            // map change entry to real CharacterCount id from create response
            //-----------------------------------------------------------------
            const counts = Array.isArray(editCharacterResult.character.counts)
              ? editCharacterResult.character.counts
              : []
            const idx = counts.findIndex((c) => c.name === s.count_name)

            console.log(
              'Resolving CharacterCount id for change entry name =',
              s.count_name,
              ' index =',
              idx,
            )

            if (idx !== -1) {
              s.id = counts[idx].id
              console.log('Updated count change entry with real id =', s.id)
            } else {
              console.warn(
                'WARN: Could not resolve CharacterCount for starter count change name =',
                s.count_name,
              )
            }
          }
        })
      }
      //-------------------------
      // call edit count harness
      //-------------------------
      await useEditCharacterCountsHarness(parms.changed_counts)
      // console.log('XXXX UPDATED COUNTS RESULTS:', updateCharacterCountsResults)
    }
    //==================================================================================================
    // H. campaign maintenance (CRUD)
    //-----------------------------
    if (
      parms.changed_campaign_notes &&
      Array.isArray(parms.changed_campaign_notes) &&
      parms.changed_campaign_notes.length > 0
    ) {
      console.log('C. IN useCharacterChangesHarness - UPDATE CAMPAIGN NOTES')
      //---------------------------------------------------
      // where new character (otherwise ids already known)
      //---------------------------------------------------
      if (!isEmpty(editCharacterResult)) {
        //-------------------
        // loop count changes
        //-------------------
        parms.changed_campaign_notes.forEach((s) => {
          console.log('campaign note before updates:', s)
          //-----------------------------
          // 2. where new character-campaign note
          //-----------------------------
          if (s.action === 'ADD' && s.payload) {
            //-----------------------------------------------------------
            // set character id in payload (id will be temp/stripped)
            //-----------------------------------------------------------
            s.payload.character = editCharacterResult.character.id
            console.log(
              '2. New character-campaign note detected, setting character id in payload = ',
              s.payload.character,
            )
          } else if (
            (s.action === 'UPDATE' || s.action === 'DELETE') &&
            (!s.id || hasTempId(s.id))
          ) {
            //-----------------------------------------------------------------
            // existing starter campaign notes for a NEW character:
            // map change entry to real CampaignNote id from create response
            //-----------------------------------------------------------------
            const campaign_notes = Array.isArray(editCharacterResult.character.campaign_notes)
              ? editCharacterResult.character.campaign_notes
              : []
            const idx = campaign_notes.findIndex((c) => c.name === s.campaign_note_name)

            console.log(
              'Resolving CampaignNote id for change entry name =',
              s.campaign_note_name,
              ' index =',
              idx,
            )

            if (idx !== -1) {
              s.id = campaign_notes[idx].id
              console.log('Updated campaign note change entry with real id =', s.id)
            } else {
              console.warn(
                'WARN: Could not resolve CampaignNote for starter campaign note change name =',
                s.campaign_note_name,
              )
            }
          }
        })
      }
      //---------------------------------
      // call edit campaign note harness
      //---------------------------------
      await useEditCharacterCampaignNotesHarness(parms.changed_campaign_notes)
      // console.log('XXXX UPDATED CAMPAIGN NOTES RESULTS:', updateCharacterCampaignNotesResults)
    }
    //==================================================================================================
    // H. armour location maintenance (update hp_cur on save only)
    //-----------------------------
    if (
      parms.changed_armour_locations &&
      Array.isArray(parms.changed_armour_locations) &&
      parms.changed_armour_locations.length > 0
    ) {
      await useUpdateCharacterArmourLocationsHarness(parms.changed_armour_locations)
    }

    if (hasInitialHpCharacteristicChanges && !isEmpty(editCharacterResult?.character?.slug)) {
      const persistedCharacter = await getPersistedCharacterOnce()
      const explicitlyChangedArmourLocationNames = new Set(
        (Array.isArray(parms.changed_armour_locations) ? parms.changed_armour_locations : [])
          .map((entry) => (entry?.armour_location_name ?? '').toString().trim().toLowerCase())
          .filter((value) => value !== ''),
      )
      const initialHpUpdates = (persistedCharacter?.armour_locations ?? [])
        .filter((location) => {
          if (!location || location.id == null) {
            return false
          }

          const locationName = (location.armour_location_name ?? '').toString().trim().toLowerCase()

          if (locationName && explicitlyChangedArmourLocationNames.has(locationName)) {
            return false
          }

          return Number(location.hp_cur) !== Number(location.hp_tot)
        })
        .map((location) => ({
          id: location.id,
          action: 'UPDATE',
          payload: {
            hp_cur: location.hp_tot,
          },
        }))

      if (initialHpUpdates.length > 0) {
        await useUpdateCharacterArmourLocationsHarness(initialHpUpdates)
        persistedCharacterCache = null
      }
    }

    if (
      isNewCharacterSave &&
      Number.isFinite(initialSaveMpCur) &&
      !isEmpty(editCharacterResult?.character?.slug)
    ) {
      await useEditCharacter({
        slug: editCharacterResult.character.slug,
        payload: {
          mp_cur: initialSaveMpCur,
        },
      })
      persistedCharacterCache = null
    }

    if (persistedCharacterCache && hasFollowOnPersistenceChanges()) {
      console.log(
        '[RECOVERABLE_ARTEFACT_TRACE] clearing persistedCharacter cache before final resolution',
        {
          cache_summary_before_clear: getPersistedCharacterTraceSummary(persistedCharacterCache),
        },
      )
      persistedCharacterCache = null
    }
    //===================================================================
    // CATCH ERRORS
    //--------------
    console.log('[RECOVERABLE_ARTEFACT_TRACE] final persistedCharacter resolution start', {
      has_cache_before_final_fetch: Boolean(persistedCharacterCache),
      cache_summary_before_final_fetch: getPersistedCharacterTraceSummary(persistedCharacterCache),
      update_item_results_count: Array.isArray(updateCharacterItemsResults)
        ? updateCharacterItemsResults.length
        : 0,
      update_rune_results_count: Array.isArray(updateCharacterRunesResults)
        ? updateCharacterRunesResults.length
        : 0,
      update_spell_results_count: Array.isArray(updateCharacterSpellsResults)
        ? updateCharacterSpellsResults.length
        : 0,
    })
    const persistedCharacter = await getPersistedCharacterOnce()

    console.log('[RECOVERABLE_ARTEFACT_TRACE] final persistedCharacter resolved', {
      persisted_character: getPersistedCharacterTraceSummary(persistedCharacter),
    })

    // Frontend safety net: where backend returns stale character-item
    // fields on the final character fetch, preserve values we just sent
    // in changed item payloads so the UI does not rollback immediately
    // after save.
    if (persistedCharacter && Array.isArray(persistedCharacter.items)) {
      const responseItemsById = new Map()
      if (Array.isArray(updateCharacterItemsResults)) {
        updateCharacterItemsResults.forEach((res) => {
          const data = res?.data
          if (data && data.id != null) {
            responseItemsById.set(Number(data.id), data)
          }
        })
      }

      responseItemsById.forEach((responseItem, id) => {
        const targetItem = findPersistedCharacterItemMatch(persistedCharacter.items, responseItem)
        if (targetItem) {
          const preservedImage =
            responseItem?.image === null ||
            responseItem?.image === undefined ||
            responseItem?.image === ''
              ? targetItem.image
              : responseItem.image

          Object.assign(targetItem, responseItem)

          if (preservedImage !== undefined) {
            targetItem.image = preservedImage
          }
        } else {
          persistedCharacter.items.push({ ...responseItem })
        }
      })
    }

    if (
      persistedCharacter &&
      Array.isArray(persistedCharacter.items) &&
      Array.isArray(parms.changed_items)
    ) {
      parms.changed_items.forEach((entry) => {
        if (!entry || entry.action === 'DELETE' || !entry.payload) return

        const targetId = entry._resolved_character_item_id ?? entry.id ?? entry.payload.id ?? null
        if (targetId == null) return

        const targetItem = persistedCharacter.items.find(
          (ci) => ci && Number(ci.id) === Number(targetId),
        )
        if (!targetItem) return

        Object.entries(entry.payload).forEach(([key, value]) => {
          if (key === 'id') {
            return
          }

          if (value !== undefined) {
            targetItem[key] = value
          }
        })

        if (Object.prototype.hasOwnProperty.call(entry.payload, 'enc')) {
          const encNum = Number(targetItem.enc)
          const qtyNum = Number(targetItem.tally_qty ?? 0)
          if (!Number.isNaN(encNum) && !Number.isNaN(qtyNum)) {
            targetItem.tot_enc = Math.round((encNum * qtyNum + Number.EPSILON) * 100) / 100
          }
        }
      })
    }

    return {
      character: persistedCharacter,
      image: editCharacterResult?.image ?? null,
    }
  } catch (err) {
    if (createdCharacterSlugForRollback) {
      try {
        console.warn(
          'Rolling back newly created character after downstream save failure:',
          createdCharacterSlugForRollback,
        )
        await useDeleteCharacter(createdCharacterSlugForRollback)
      } catch (rollbackErr) {
        console.error('Rollback delete failed for newly created character:', rollbackErr)
      }
    }

    console.error('Error in useCharacterChangesHarness:', err)
    throw err // propagate error
  }
}
